import React from "react";
import styled, { keyframes } from "styled-components";
import { FiArrowRight } from "react-icons/fi";
import Button from "./shared/Button";
import { Link } from "react-router-dom";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

// Styled Components
const FullWidthSection = styled.section`
  width: 100%;
  background: linear-gradient(135deg, #f8f9ff, #f0f7ff);
  padding: 100px 0;
  position: relative;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding: 0 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1200px) {
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 30px;
  }
`;

const MainContainer = styled.div`
  width: 100%;
  padding: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(145deg, #f8f9ff, #ffffff);
  border-radius: 24px;
  box-shadow: 0 30px 60px -10px rgba(21, 28, 72, 0.15);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(90deg, #151c48, #5ab9d3);
    border-radius: 24px 24px 0 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 40px 30px;
    margin: 0 auto;
  }
`;

const TextContainer = styled.div`
  max-width: 55%;
  margin-left: 40px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    max-width: 100%;
    margin-left: 0;
    margin-top: 30px;
  }
`;

const Title = styled.h1`
  font-size: 2.8rem;
  font-weight: 800;
  color: #151c48;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
  position: relative;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;

  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #5ab9d3, #151c48);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
    text-align: center;

    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const SubTitle = styled.h2`
  font-size: 1.5rem;
  color: #5f6c84;
  margin-bottom: 30px;
  font-weight: 600;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    text-align: center;
  }
`;

const ImageContainer = styled.div`
  max-width: 40%;
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.8s ease-out;

  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: 30px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 20px 50px -10px rgba(21, 28, 72, 0.2);
  transition: transform 0.5s ease;
  animation: ${floatAnimation} 6s ease-in-out infinite;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: #5f6c84;
  line-height: 1.8;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// const ContactButton = styled.button`
//   background: linear-gradient(90deg, #151c48, #2a3480);
//   color: white;
//   padding: 16px 32px;
//   border: none;
//   border-radius: 12px;
//   font-weight: 600;
//   font-size: 1.1rem;
//   cursor: pointer;
//   transition: all 0.3s ease;
//   display: inline-flex;
//   align-items: center;
//   box-shadow: 0 5px 15px rgba(21, 28, 72, 0.2);
//   position: relative;
//   overflow: hidden;
//   z-index: 1;

//   &:hover {
//     transform: translateY(-2px);
//     box-shadow: 0 8px 20px rgba(21, 28, 72, 0.3);

//     svg {
//       transform: translateX(5px);
//     }
//   }

//   svg {
//     margin-left: 10px;
//     transition: transform 0.3s ease;
//   }

//   &::before {
//     content: '';
//     position: absolute;
//     top: 0;
//     left: 0;
//     width: 100%;
//     height: 100%;
//     background: linear-gradient(90deg, #2a3480, #151c48);
//     opacity: 0;
//     transition: opacity 0.3s ease;
//     z-index: -1;
//   }

//   &:hover::before {
//     opacity: 1;
//   }

//   @media (max-width: 768px) {
//     width: 100%;
//     justify-content: center;
//     padding: 14px 28px;
//   }
// `;

const DecorativeElement = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(90, 185, 211, 0.1), rgba(21, 28, 72, 0.05));
  filter: blur(60px);
  z-index: 0;

  &:nth-child(1) {
    top: -150px;
    left: -150px;
  }

  &:nth-child(2) {
    bottom: -100px;
    right: -100px;
  }
`;

function AboutUs() {
  return (
    <FullWidthSection>
      <ContentContainer>
        <MainContainer>
          <DecorativeElement />
          <DecorativeElement />
          
          <ImageContainer>
            <Image
              src="/assets/pessoastrabalhando.jpg"
              alt="Equipe trabalhando na realocação para Portugal"
            />
          </ImageContainer>

          <TextContainer>
            <Title>Sobre Nós</Title>
            <SubTitle>Bem-vindo à nossa plataforma de realocação para Portugal!</SubTitle>

            <Description>
              Nossa missão é ajudar expatriados a encontrar os melhores serviços de realocação para Portugal. Oferecemos uma plataforma que conecta usuários a empresas de realocação, com avaliações, detalhes de contato e informações sobre o processo de obtenção de visto. Se você está se mudando para Portugal, nosso objetivo é facilitar sua jornada com a melhor experiência possível.
              <br /><br />
              A plataforma oferece uma busca simples para encontrar empresas confiáveis, além de fornecer todas as informações necessárias sobre tipos de vistos, processos de aplicação e recursos úteis. Estamos comprometidos em tornar o processo de realocação mais fácil, transparente e seguro para todos os nossos usuários.
            </Description>

            <Button
              as={Link}
              to="/contact"
              primary
              style={{ margin: '0 auto' }}
            >

              Entre em Contato <FiArrowRight style={{ transition: 'transform 0.3s ease' }} />
            </Button>
          </TextContainer>
        </MainContainer>
      </ContentContainer>
    </FullWidthSection>
  );
}

export default AboutUs;